import React from "react";
import { Box, createStyles } from "@mantine/core";
import { box, metaLine, isImageSignature } from "./pdfHelpers";

// The actual document layout (title through signature) — mirrors the
// prototype's #pdf-viewer .pdf-page content exactly. Used both for the
// on-screen preview (inside PdfPreviewModal's gray viewer) and, unmodified,
// as the source html2canvas captures for the real downloaded PDF — so the
// download is pixel-for-pixel what was previewed, not a separate redraw.
const useStyles = createStyles(() => ({
  page: {
    width: 640,
    background: "#fff",
    padding: "40px 48px 64px",
    color: "#1a1a1a",
  },
  pageFloating: {
    boxShadow: "0 4px 18px rgba(0,0,0,.2)",
    borderRadius: 2,
  },
  title: { fontSize: 20, fontWeight: 800, letterSpacing: "-0.01em" },
  subtitle: { fontSize: 11.5, color: "#666", marginTop: 3 },
  hr: { border: "none", borderTop: "1px solid #e5e5e5", margin: "16px 0" },
  hgrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 14,
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    padding: "14px 16px",
  },
  hgridPaired: { gridTemplateColumns: "1fr 1fr" },
  hlabel: { fontSize: 9, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: "#8a8a8a" },
  hval: { fontSize: 12.5, fontWeight: 700, marginTop: 3 },
  sect: {
    background: "#f2f2f2",
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: "#1a1a1a",
    padding: "7px 11px",
    borderRadius: 4,
    margin: "24px 0 14px",
  },
  outcome: { marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid #eee", "&:last-of-type": { borderBottom: "none" } },
  otitle: { fontSize: 13, fontWeight: 700 },
  otitleTag: { fontWeight: 400, color: "#777" },
  ometa: { fontSize: 10.5, color: "#777", margin: "2px 0 9px" },
  checks: { display: "flex", gap: 20, fontSize: 12, marginBottom: 9, flexWrap: "wrap" },
  check: { display: "flex", alignItems: "center", gap: 6, color: "#333" },
  checkOn: { fontWeight: 700, color: "#111" },
  bx: { fontSize: 14, lineHeight: 1 },
  flabel: { fontSize: 9, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#8a8a8a", marginBottom: 3 },
  narr: { fontSize: 12, lineHeight: 1.55, color: "#222" },
  narrEmpty: { fontStyle: "italic", color: "#999" },
  plan: { fontSize: 11.5, color: "#333", marginTop: 8 },
  supportGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 20px", fontSize: 12, color: "#333", marginTop: 6 },
  supportItem: { display: "flex", alignItems: "center", gap: 7 },
  q: { marginBottom: 14 },
  qtext: { fontSize: 12, fontWeight: 600, color: "#111", marginBottom: 5 },
  sig: { display: "grid", gridTemplateColumns: "1.4fr 1.4fr 1fr", gap: 24, marginTop: 20 },
  sigLine: {
    borderBottom: "1px solid #333",
    height: 26,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    paddingBottom: 3,
    fontSize: 12,
    color: "#1a1a1a",
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  sigScript: { fontFamily: '"Segoe Script","Snell Roundhand","Brush Script MT",cursive', fontStyle: "italic", fontSize: 18 },
  sigImage: { maxHeight: 24, maxWidth: "100%", objectFit: "contain" },
  sigCap: { fontSize: 9.5, color: "#666", marginTop: 5, textTransform: "uppercase", letterSpacing: ".05em", textAlign: "left" },
  requiresSel: {
    marginLeft: 8,
    fontSize: 9.5,
    fontWeight: 700,
    color: "#B45309",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    whiteSpace: "nowrap",
  },
}));

const Check = ({ on, label, classes, cx }) => (
  <span className={cx(classes.check, on && classes.checkOn)}>
    <span className={classes.bx}>{box(on)}</span> {label}
  </span>
);

// A selection the engine could not evidence is printed as an open item rather
// than silently rendering as "all boxes unticked", which on a signed form would
// read as a deliberate answer.
const RequiresSel = ({ classes }) => (
  <span className={classes.requiresSel}>Requires QDDP selection</span>
);

const pdfDate = (v) => {
  if (!v) return "__________";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

// `plain`: drop the floating drop-shadow/rounded-corner look used in the
// on-screen viewer — the offscreen capture used for the real PDF wants a
// flush page, since the shadow would otherwise get baked into every exported
// page image.
const PdfDocument = ({ draft, plain }) => {
  const { classes, cx } = useStyles();
  // Reports drafted before Section B became plural carry a single `supportB`.
  const risksB = draft.risksB?.length ? draft.risksB : draft.supportB ? [draft.supportB] : [];
  // A manual report has no engine selections and no plan behind it: there is no
  // note provenance to cite, no plan effective dates to print, and nothing the
  // engine could have failed to evidence — so the "Requires QDDP selection"
  // markers and those meta lines are omitted rather than printed empty on a
  // document that gets signed and filed.
  const isManual = draft.source === "manual";
  const hasFacility = isManual && !!draft.facilityName;

  return (
    <Box className={cx(classes.page, !plain && classes.pageFloating)}>
      {/* draft.title is server-computed from the "Report title format"
          setting (see toFullReport() in quarterlyPcr.service.js) — falls
          back to the DBHDS document name only if that field is ever absent
          (e.g. an older cached draft shape). */}
      <Box className={classes.title}>{draft.title || "Quarterly Person-Centered Review"}</Box>
      <Box className={classes.subtitle}>
        Virginia DBHDS · Individual Support Plan v4.0 · Outcome and Essential Supports Review
      </Box>
      <hr className={classes.hr} />
      {/* Four fields in a three-column grid left Facility / Home stranded alone
          on a second row. With a facility the header pairs off 2×2 — Individual
          → Facility, Provider → Review Period — the same reading order as the
          review screen's Report details block; without one the three fields
          still fill a single three-column row. */}
      <Box className={cx(classes.hgrid, hasFacility && classes.hgridPaired)}>
        <Box>
          <Box className={classes.hlabel}>Individual</Box>
          <Box className={classes.hval}>{draft.clientName}</Box>
        </Box>
        {hasFacility && (
          <Box>
            <Box className={classes.hlabel}>Facility / Home</Box>
            <Box className={classes.hval}>{draft.facilityName}</Box>
          </Box>
        )}
        <Box>
          <Box className={classes.hlabel}>Provider / Service</Box>
          <Box className={classes.hval}>{draft.providerName}</Box>
        </Box>
        <Box>
          <Box className={classes.hlabel}>Review Period</Box>
          <Box className={classes.hval}>{draft.periodLabel}</Box>
        </Box>
      </Box>

      <Box className={classes.sect}>Section A · Desired Outcomes</Box>

      {draft.outcomesA.map((o) => (
        <Box key={o.id} className={classes.outcome}>
          <Box className={classes.otitle}>{o.title}</Box>
          {!isManual && <Box className={classes.ometa}>{metaLine(o)}</Box>}
          <Box className={classes.checks}>
            <Check classes={classes} cx={cx} on={o.status === "achieved"} label="Achieved" />
            <Check classes={classes} cx={cx} on={o.status === "on-track"} label="On track" />
            <Check classes={classes} cx={cx} on={o.status === "limited"} label="Limited or no progress" />
            {o.status == null && !isManual && <RequiresSel classes={classes} />}
          </Box>
          <Box className={classes.flabel}>Status description</Box>
          <Box className={classes.narr}>{o.description}</Box>
          <Box className={classes.plan}>
            <strong>Plan change needed?</strong> {box(o.planChangeNeeded === true)} Yes&nbsp;&nbsp;
            {box(o.planChangeNeeded === false)} No
            {o.planChangeNeeded === true && o.planChangeDescription ? ` — ${o.planChangeDescription}` : ""}
            {o.planChangeNeeded == null && !isManual && <RequiresSel classes={classes} />}
          </Box>
        </Box>
      ))}

      <Box className={classes.sect}>Section B · Essential Supports</Box>

      {/* One row per plan essential support classified as a risk. Routine
          Support rows are not risks — they render in the checklist below. */}
      {risksB.map((r) => (
        <Box key={r.id} className={classes.outcome}>
          <Box className={classes.otitle}>
            {/* The support statement — what the risk is and how to support it —
                not the category, which is tagged separately. */}
            {r.title}{" "}
            {r.riskType ? (
              <span className={classes.otitleTag}>
                · {r.riskType === "identified" ? "Identified Risk" : "Potential Risk"}
              </span>
            ) : isManual ? null : (
              <span className={classes.otitleTag}>· Requires QDDP selection</span>
            )}
          </Box>
          {!isManual && (
            <Box className={classes.ometa}>
              Start: {pdfDate(r.effectivePeriods?.start)} &nbsp;·&nbsp; End: {pdfDate(r.effectivePeriods?.end)}
              {r.howOften ? ` · Frequency: ${r.howOften}` : ""}
            </Box>
          )}
          <Box className={classes.checks}>
            <Check classes={classes} cx={cx} on={r.status === "achieved"} label="Achieved" />
            <Check classes={classes} cx={cx} on={r.status === "on-track"} label="On track" />
            <Check classes={classes} cx={cx} on={r.status === "limited"} label="Limited or no progress" />
            {r.status == null && !isManual && <RequiresSel classes={classes} />}
          </Box>
          <Box className={classes.flabel}>Status description</Box>
          <Box className={classes.narr}>{r.description}</Box>
          <Box className={classes.plan}>
            <strong>Plan change needed?</strong> {box(r.planChangeNeeded === true)} Yes&nbsp;&nbsp;
            {box(r.planChangeNeeded === false)} No
            {r.planChangeNeeded === true && r.planChangeDescription ? ` — ${r.planChangeDescription}` : ""}
            {r.planChangeNeeded == null && !isManual && <RequiresSel classes={classes} />}
          </Box>
        </Box>
      ))}

      <Box className={classes.flabel} mt={6}>
        Routine supports documented this period
      </Box>
      <Box className={classes.supportGrid}>
        {draft.checklistB.map((s) => (
          <Box key={s.id} className={classes.supportItem}>
            {box(s.checked)} {s.label}
            {s.checked && s.note ? ` — ${s.note}` : ""}
          </Box>
        ))}
      </Box>

      <Box className={classes.sect}>Section C · Review Questions</Box>
      {draft.questionsC.map((q, i) => (
        <Box key={q.id} className={classes.q}>
          <Box className={classes.qtext}>
            {i + 1}. {q.text} &nbsp; {box(q.answer === "yes")} Yes&nbsp;&nbsp;{box(q.answer === "no")} No
            {q.answer == null && !isManual && <RequiresSel classes={classes} />}
          </Box>
          <Box className={classes.narr}>
            <span className={q.note?.startsWith("Insufficient") ? classes.narrEmpty : undefined}>{q.note}</span>
          </Box>
        </Box>
      ))}

      <Box className={classes.sect}>Completion</Box>
      <Box className={classes.narr} mb={4}>
        ISP revision date(s):{" "}
        {!draft.ispRevisionDates || draft.ispRevisionDates === "None recorded"
          ? "______________ ; ______________ ; ______________"
          : draft.ispRevisionDates}
      </Box>
      <Box className={classes.sig}>
        <Box>
          <Box className={classes.sigLine}>{draft.signature?.nameLine || ""}</Box>
          <Box className={classes.sigCap}>Completed by (print)</Box>
        </Box>
        <Box>
          <Box className={classes.sigLine}>
            {isImageSignature(draft.signature?.signature) ? (
              <img
                src={draft.signature.signature}
                alt="signature"
                crossOrigin="anonymous"
                className={classes.sigImage}
              />
            ) : (
              <span className={classes.sigScript}>{draft.signature?.signature || ""}</span>
            )}
          </Box>
          <Box className={classes.sigCap}>Signature</Box>
        </Box>
        <Box>
          <Box className={classes.sigLine}>{draft.signature?.date || ""}</Box>
          <Box className={classes.sigCap}>Date</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PdfDocument;
